/**
 * Utility functions for score formatting, display, and user score management
 */

import { ST } from '../types';
import { rankerService } from '../services/freak_ranker';
import { logger } from '../services/Logger';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from './constants';

/**
 * Format a score to one decimal place
 * @param score - The score to format
 * @returns Formatted score string
 */
export const formatScore = (score: number | undefined): string => {
  if (score === undefined || score === null) return '0.0';
  return (Math.round(score * 10) / 10).toFixed(1);
};

/**
 * Calculate total score from a list of tricks
 * @param tricks - Array of tricks with potential scoring data
 * @returns Total calculated score
 */
export const calculateTotalScore = (tricks: any[]): number => {
  let totalScore = 0;
  tricks.forEach((trick) => {
    if (trick.playData?.ranking?.play_score?.total_score) {
      totalScore += trick.playData.ranking.play_score.total_score;
    }
  });
  return totalScore;
};

/**
 * Get display score from trick data
 * @param figureData - Run data object
 * @returns Score number or 0 if not found
 */
export const getRunScore = (figureData: any): number => {
  return figureData?.playData?.ranking?.play_score?.total_score || 0;
};

/**
 * Get formatted figure score from figure data
 * @param figureData - Figure data object
 * @returns Formatted score string
 */
export const getFigureScore = (figureData: any): string => {
  const score = figureData?.computed_score || 0;
  return formatScore(score);
};

/**
 * Update user score after successful play submission
 * @param userId - User ID
 * @param playScore - The score from the current play
 * @param currentUser - Current user instance from store
 * @returns Promise<boolean> - Success status
 */
export async function updateUserScoreAfterPlay(
  userId: string,
  playScore: number,
  currentUser: any
): Promise<boolean> {
  try {
    if (!currentUser || currentUser.uid !== userId) {
      return false;
    }

    // Nur speichern, wenn der neue Score besser ist als der aktuelle
    if (typeof currentUser.score === 'number') {
      const newScore = currentUser.score + playScore / 100;
      await currentUser.updateScore(newScore);
      logger.score('[ScoreDebug] Neuer Score wurde geschrieben:', newScore);
      logger.success('✅ User score updated:', {
      previousScore: currentUser.score,
      newScore,
      });
      return true;
    } else {
      logger.score(
      '[ScoreDebug] Score wurde NICHT geschrieben. Aktueller Score:',
      currentUser.score,
      'PlayScore:',
      playScore
      );
      logger.info('ℹ️ User score not updated (invalid current score)', {
      currentScore: currentUser.score,
      playScore,
      });
      return false;
    }
  } catch (error) {
    logger.error('❌ Failed to update user score after play:', error);
    return false;
  }
}

/**
 * Refresh user's total score from all plays
 * @param userId - User ID
 * @param currentUser - Current user instance from store
 * @returns Promise<boolean> - Success status
 */
export async function refreshUserTotalScore(
  userId: string,
  currentUser: any
): Promise<boolean> {
  try {
    if (!currentUser || currentUser.uid !== userId) {
      return false;
    }

    // Recalculate score from all user's plays
    const newScore = await currentUser.recalculateScore();
    return true;
  } catch (error) {
    logger.error('❌ Failed to refresh user total score:', error);
    return false;
  }
}

/**
 * Get user's current ranking position
 * @param userId - User ID
 * @returns Promise<number | null> - Ranking position (1-based) or null if not found
 */
export async function getUserRankingPosition(
  userId: string
): Promise<number | null> {
  try {
    // Get global ranking
    const globalRanking = await rankerService.getGlobalRanking(0, 100);

    // Find user's position
    const userPosition = globalRanking.findIndex(
      (user) => user.uid === userId || user.id === userId
    );

    if (userPosition !== -1) {
      const position = userPosition + 1;
      return position;
    } else {
      return null;
    }
  } catch (error) {
    logger.error('❌ Failed to get user ranking position:', error);
    return null;
  }
}

/**
 * Show score update notification
 * @param previousScore - Previous user score
 * @param newScore - New user score
 * @param playScore - Score from current play
 * @param present - Toast present function
 */
export function showScoreUpdateNotification(
  previousScore: number,
  newScore: number,
  playScore: number,
  present: (options: any) => void
) {
  const scoreDifference = newScore - previousScore;
  const isNewBest = scoreDifference > 0;

  let message = `🎯 Play Score: ${playScore.toFixed(1)} points`;

  if (isNewBest) {
    message += `\n🎉 New Total: ${newScore.toFixed(
      1
    )} (+${scoreDifference.toFixed(1)})`;
  } else {
    message += `\n📊 Total Score: ${newScore.toFixed(1)}`;
  }

  present({
    message,
    duration: MESSAGE_ALIVE_MS,
    color: isNewBest ? 'success' : 'primary',
    position: MESSAGE_POSITION,
    buttons: [
      {
        text: 'View Rankings',
      },
    ],
  });
}

export function isBackendFigureScore(score: any): score is {
  computed_score: number;
  achieved: boolean;
  difficulty_rating?: number;
} {
  return (
    score &&
    typeof score === 'object' &&
    typeof score.computed_score === 'number' &&
    typeof score.achieved === 'boolean'
  );
}

export function isCompatibleFigureScore(score: any): score is ST.FigureScore {
  return (
    score &&
    typeof score === 'object' &&
    typeof score.figure_index === 'number' &&
    typeof score.figure_name === 'string' &&
    typeof score.computed_score === 'number' &&
    typeof score.achieved === 'boolean'
  );
}

// ✅ Score-Conversion Utilities
export function convertBackendToCompatibleScore(
  backendScore: any,
  index: number,
  figureName: string = '',
  timeRange?: { start: number; end: number }
): ST.FigureScore {
  if (!isBackendFigureScore(backendScore)) {
    throw new Error(`Invalid backend score structure at index ${index}`);
  }

  return {
    figure_index: index,
    figure_name: figureName || `Figure ${index + 1}`,
    figure_name_normalized: (figureName || `figure_${index + 1}`)
      .toLowerCase()
      .replace(/\s+/g, '_'),
    matrix_found: true,
    time_range: timeRange || { start: 0, end: 0 },
    computed_score: backendScore.computed_score,
    achieved: backendScore.achieved,
    difficulty_rating: backendScore.difficulty_rating || 0,
    performance_quality: (backendScore as any).performance_quality || 0,
    execution_quality: (backendScore as any).execution_quality || 0,
    technical_difficulty: (backendScore as any).technical_difficulty || 0,
  };
}

export function convertBackendRankingToCompatible(
  backendRanking: any,
  figures?: Array<{ name?: string }>
): ST.RankingResponse {
  const compatibleFigureScores: ST.FigureScore[] = [];

  if (
    backendRanking.figure_scores &&
    Array.isArray(backendRanking.figure_scores)
  ) {
    backendRanking.figure_scores.forEach((backendScore: any, index: number) => {
      try {
        const figureName = figures?.[index]?.name || '';
        const compatibleScore = convertBackendToCompatibleScore(
          backendScore,
          index,
          figureName
        );
        compatibleFigureScores.push(compatibleScore);
      } catch (error) {
        logger.error(
          `Failed to convert figure score at index ${index}:`,
          error
        );
      }
    });
  }

  return {
    play_score: {
      total_score: backendRanking.play_score?.total_score || 0,
      figures_achieved: backendRanking.play_score?.figures_achieved || 0,
      figure_count: backendRanking.play_score?.figure_count || 0,
    },
    figure_scores: compatibleFigureScores,
    version: backendRanking.version || '1.0.0',
  };
}
